import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useWizard } from 'react-use-wizard';
import axios from 'axios';
import get from 'lodash/get';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import FileSaver from 'file-saver';
import { addParametersToUri } from 'nallian-shared-ui/lib/lib/uri';
import { Loader, Flex, Button, Checkbox } from 'nallian-shared-ui/lib/components';
import { GetCommunityTag, GetUserLoading } from 'nallian-shared-ui/lib/selectors';
import { Text, ErrorIcon } from 'nallian-shared-ui/lib/layout/styles';
import communityService from '../../../app/services/communityService';
import communityLegalDocumentsService from '../../services/communityLegalDocumentsService';
import PdfViewer from '../../../app/components/PdfViewer';
import apiRoutes from '../../../../app/api/routes';
import { LegalDocumentContainer } from '../../styles';
import { toBase64 } from '../../../app/helpers/toBase64';
import { b64toFile } from '../../../app/helpers/base64ToFile';
import { ORGANISATION_STATES } from '../../../app/constants';

const LegalDocuments = ({
  dispatchCommunityLegalDocumentsGet,
  communityLegalDocumentsFetch,
  handleCompleteInvitation,
  completeInvitationFetch,
  invitationFetch,
  data,
}) => {
  const { t, i18n } = useTranslation();
  const { nextStep } = useWizard();
  const tag = useSelector(GetCommunityTag);
  const isLoadingUser = useSelector(GetUserLoading);

  const [accepted, setAccepted] = useState(false);
  const [documentContent, setDocumentContent] = useState();
  const [isDownloadingContent, setIsDownloadingContent] = useState(true);

  useEffect(() => {
    const organisationState = get(invitationFetch.value, 'organisation.activeStateId');
    if (!isLoadingUser && organisationState === ORGANISATION_STATES.ACTIVE) {
      handleCompleteInvitation({}, nextStep);
    }
  }, [isLoadingUser, invitationFetch.value, handleCompleteInvitation, nextStep]);

  useEffect(() => {
    if (tag) dispatchCommunityLegalDocumentsGet(tag);
  }, [dispatchCommunityLegalDocumentsGet, tag]);

  const firstLegalDoc = useMemo(() => get(communityLegalDocumentsFetch.value, '[0]'), [
    communityLegalDocumentsFetch.value,
  ]);

  useEffect(() => {
    const fetchDoc = async () => {
      if (firstLegalDoc) {
        const response = await axios.get(
          addParametersToUri(apiRoutes.legalDocumentsDownload(firstLegalDoc.id), {
            contentLanguageId: i18n.language,
            revision: firstLegalDoc.alwaysGetLatestVersion ? null : firstLegalDoc.revision,
          }),
          {
            headers: { Accept: 'application/octet-stream' },
            responseType: 'blob',
          }
        );
        const base64 = await toBase64(response.data);
        setDocumentContent(base64);
        setIsDownloadingContent(false);
      }
    };

    fetchDoc();
  }, [
    setIsDownloadingContent,
    setDocumentContent,
    communityLegalDocumentsFetch.value,
    i18n.language,
    firstLegalDoc,
  ]);

  const handleDownloadFiles = useCallback(() => {
    const fileName = `${firstLegalDoc.title}.pdf`;
    const base64Content = get(documentContent.split('data:application/pdf;base64,'), '[1]');
    FileSaver.saveAs(b64toFile(base64Content, fileName), fileName);
  }, [firstLegalDoc, documentContent]);

  const handleSetAccepted = useCallback((_, { checked }) => setAccepted(checked), [setAccepted]);

  const handleSignDocuments = useCallback(() => {
    const documentsToAccept = communityLegalDocumentsFetch.value || [];

    handleCompleteInvitation(
      {
        organisation: data,
        legalDocuments: documentsToAccept.map(d => ({
          legalDocumentId: d.id,
          revision: d.revision,
          languageId: 'en',
        })),
        communityTag: tag,
      },
      nextStep
    );
  }, [handleCompleteInvitation, tag, data, communityLegalDocumentsFetch, nextStep]);

  if (
    completeInvitationFetch.pending ||
    communityLegalDocumentsFetch.pending ||
    isDownloadingContent
  ) {
    return <Loader inline active />;
  }

  if (completeInvitationFetch.rejected) {
    return (
      <Flex direction="column" alignItems="center">
        <ErrorIcon name="close" color="white" size="large" />
        <Flex direction="column" alignItems="center" margin="1rem 0 0">
          <Text>{t('sometingWentWrong')}</Text>
          <Text>
            <Trans
              i18nKey="couldNotCompleteInvitation"
              values={{ text: t('here') }}
              components={[
                <br />,
                <a
                  href="https://nallian.zendesk.com/anonymous_requests/new"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('here')}
                </a>,
              ]}
            />
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex direction="column">
      <Text>{t('pleaseAcceptOurTermsAndConditions')}</Text>
      <LegalDocumentContainer>
        <PdfViewer content={documentContent} />
      </LegalDocumentContainer>
      <Flex alignItems="center" margin="0 0 20px 0">
        <Flex fluid>
          <Checkbox label={t('readAllLegalDocuments')} onClick={handleSetAccepted} />
        </Flex>
        <Flex justifyContent="flex-end">
          <Button onClick={handleDownloadFiles} icon="download" />
        </Flex>
      </Flex>
      <Flex fluid margin="1rem 0 0" justifyContent="flex-end">
        <Button content="Agree" disabled={!accepted} onClick={handleSignDocuments} btnprimary />
      </Flex>
    </Flex>
  );
};

LegalDocuments.propTypes = {
  dispatchCommunityLegalDocumentsGet: PropTypes.func.isRequired,
  communityLegalDocumentsFetch: PropTypes.object.isRequired,
  handleCompleteInvitation: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  invitationFetch: PropTypes.object.isRequired,
  completeInvitationFetch: PropTypes.object.isRequired,
};

export default compose(communityService, communityLegalDocumentsService)(LegalDocuments);
